import { Checkbox, Stack } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { isNullish } from 'remeda';
import {
  type UpdateTaskForm,
  UpdateTaskFormSchema,
  useUpdateTaskSettings,
} from 'shared/hooks/use-update-task-settings';

export function TaskSettings() {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const { updateTaskSetting, isUpdateTaskSettingLoading } = useUpdateTaskSettings();
  const form = useForm<UpdateTaskForm>({
    validate: schemaResolver(UpdateTaskFormSchema, { sync: true }),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (isNullish(company)) return;

    form.setInitialValues({ autoclose_tasks: company.autoclose_tasks });
    form.setValues({ autoclose_tasks: company.autoclose_tasks });
  }, [company?.autoclose_tasks]);

  const handleSave = () => {
    updateTaskSetting(form.getValues(), {
      onSuccess: () => {
        form.resetDirty();
        notifications.show({
          color: 'green',
          message: t(i18nKeys.FORM.PREFERENCES.TASKS.SUCCESSFULLY_UPDATE),
        });
      },
    });
  };

  return (
    <Panel
      title={t(i18nKeys.FORM.PREFERENCES.TASKS.TITLE)}
      withBorder
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateTaskSettingLoading}
          hideIcon
        />
      }
    >
      <Stack my="md">
        <Checkbox
          label={t(i18nKeys.FORM.PREFERENCES.TASKS.AUTO_UPDATE_LABEL)}
          description={t(i18nKeys.FORM.PREFERENCES.TASKS.AUTO_UPDATE_HINT)}
          {...form.getInputProps('autoclose_tasks', { type: 'checkbox' })}
        />
      </Stack>
    </Panel>
  );
}
